import React, { useState, useEffect } from 'react';
import { FileText, Upload, X, Trash2, Download, Eye, Calendar, Database } from 'lucide-react';
import api from '../services/api';

const Dossiers = () => {
  // Get userRole FIRST, before any useState
  const userRole = localStorage.getItem('userRole') || '';
  
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFilePath, setUploadedFilePath] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [activeTab, setActiveTab] = useState(userRole === 'state' ? 'training-modules' : 'mom');
const [uploadForm, setUploadForm] = useState({
  category: '',
  name: '',
  date: '',
  thumbnail: '',
  year: ''
});
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  const [uploadedThumbnailPath, setUploadedThumbnailPath] = useState(null);

  // Tab configuration - different for state vs others
  const tabs = userRole === 'state' 
    ? [
        { id: 'training-modules', label: 'Training Modules', category: 'Training Modules' }
      ]
: [
        { id: 'mom', label: 'Minutes of Meeting (MoM)', category: 'MoM' },
        { id: 'inspection', label: 'Inspection Notes', category: 'Inspection Notes' },
        { id: 'review', label: 'Review meeting & Training particulars  presentation', category: 'Review meeting & Training particulars  presentation' },
        { id: ' acts rules', label: 'Acts, Rules & GOs', category: 'Acts, Rules & GOs' },
        { id: 'training-material', label: 'Training Material', category: 'Training Material' },
        { id: 'database', label: 'Database', category: 'Database' }
      ];

  // Get current tab info
  const getCurrentTab = () => tabs.find(tab => tab.id === activeTab);

  // Fetch documents on component mount and when active tab changes
  useEffect(() => {
    fetchDocuments();
  }, [activeTab]);

  // Auto-set category when tab changes
  useEffect(() => {
    const currentTab = getCurrentTab();
    if (currentTab) {
      setUploadForm(prev => ({ ...prev, category: currentTab.category }));
    }
  }, [activeTab]);

// Filter documents by current tab category
const getFilteredDocuments = () => {
  const currentTab = getCurrentTab();
  if (!currentTab) return documents;
  
  return documents.filter(doc => 
    doc.category === currentTab.category
  );
};

  // ============= VIEW AND DOWNLOAD FUNCTIONS ============= //
  
  // Handle View - Opens the PDF in a new tab
  const handleView = (document) => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') return;
    
    const url = `https://crd-training.in/uploads/${document.file}`;
    window.open(url, '_blank');
  };

  // Handle download for regular documents
  const handleDownload = (doc) => {
    const url = `https://crd-training.in/uploads/${doc.file}`;
    
    // Create a hidden iframe to trigger download
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    iframe.style.width = '0';
    iframe.style.height = '0';
    
    // Set the source to trigger download
    iframe.src = url;
    
    // Append to body
    document.body.appendChild(iframe);
    
    // Clean up the iframe after some time
    setTimeout(() => {
      try {
        document.body.removeChild(iframe);
      } catch (e) {
        // Ignore if already removed
      }
    }, 5000);
    
    // Also try the traditional method as backup
    setTimeout(() => {
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name.endsWith('.pdf') ? doc.name : `${doc.name}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }, 1000);
  };

  // Alternative download method if the above doesn't work
  const handleDownloadAlternative = (doc) => {
    try {
      const url = `https://crd-training.in/uploads/${doc.file}`;
      
      // Create a temporary anchor element
      const anchor = window.document.createElement('a');
      anchor.style.display = 'none';
      anchor.href = url;
      anchor.download = doc.name.endsWith('.pdf') ? doc.name : `${doc.name}.pdf`;
      
      // Trigger download
      window.document.body.appendChild(anchor);
      anchor.click();
      
      // Cleanup
      setTimeout(() => {
        window.document.body.removeChild(anchor);
      }, 100);
    } catch (error) {
      console.error('Alternative download failed:', error);
      // Last resort - open in new window
      window.open(`https://crd-training.in/uploads/${doc.file}`, '_blank');
    }
  };

  // ============= END VIEW AND DOWNLOAD FUNCTIONS ============= //

const handleThumbnailSelect = async (event) => {
  const file = event.target.files[0];
  if (file) {
    if (!file.type.startsWith('image/')) {
      setError('Please select only image files for thumbnail');
      return;
    }
    
    setSelectedThumbnail(file);
    setError(null);
    await uploadThumbnail(file);
  }
};

  const uploadThumbnail = async (file) => {
    try {
      setUploading(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${api.BASE_URL}/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('authToken')
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Thumbnail upload failed');
      }

      const data = await response.json();
      
      if (data && !data.error && data.file) {
        setUploadedThumbnailPath(data.file);
        setSuccess('Thumbnail uploaded successfully!');
      } else {
        throw new Error(data.message || 'Thumbnail upload failed');
      }
    } catch (err) {
      setError('Thumbnail upload error: ' + err.message);
      console.error('Thumbnail upload error:', err);
      setSelectedThumbnail(null);
      setUploadedThumbnailPath(null);
      const thumbnailInput = window.document.getElementById('thumbnail-upload');
      if (thumbnailInput) thumbnailInput.value = '';
    } finally {
      setUploading(false);
    }
  };

  const removeThumbnail = () => {
    setSelectedThumbnail(null);
    setUploadedThumbnailPath(null);
    const thumbnailInput = window.document.getElementById('thumbnail-upload');
    if (thumbnailInput) thumbnailInput.value = '';
  };

  // Fetch documents from API
  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${api.BASE_URL}/admin/uploads`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch dossiers');
      }

      const data = await response.json();
      console.log('API Response:', data);

      if (data && data.data && Array.isArray(data.data) && data.data.length > 0) {
        setDocuments(data.data);
        setError(null);
        console.log('Dossiers loaded:', data.data.length);
      } else {
        setError(null);
        setDocuments([]);
        console.log('No dossiers found');
      }
    } catch (err) {
      setError('Error fetching dossiers: ' + err.message);
      console.error('Error fetching dossiers:', err);
    } finally {
      setLoading(false);
    }
  };

const handleFileSelect = async (event) => {
  const file = event.target.files[0];
  if (file) {
    if (file.type !== 'application/pdf') {
      setError('Please select only PDF files');
      return;
    }
    
    setSelectedFile(file);
    setError(null);
    setSuccess(null);
    
    if (!uploadForm.name) {
      const fileName = file.name.replace('.pdf', '');
      setUploadForm(prev => ({ ...prev, name: fileName }));
    }

    await uploadFile(file);
  }
};

  // Upload file to get the path
  const uploadFile = async (file) => {
    try {
      setUploading(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${api.BASE_URL}/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('authToken')
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'File upload failed');
      }

      const data = await response.json();
      
      if (data && !data.error && data.file) {
        setUploadedFilePath(data.file);
        setSuccess('File uploaded successfully! Now fill in the details and submit.');
      } else {
        throw new Error(data.message || 'File upload failed');
      }
    } catch (err) {
      setError('File upload error: ' + err.message);
      console.error('File upload error:', err);
      setSelectedFile(null);
      setUploadedFilePath(null);
      const fileInput = window.document.getElementById('file-upload');
      if (fileInput) fileInput.value = '';
    } finally {
      setUploading(false);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setUploadedFilePath(null);
    const fileInput = window.document.getElementById('file-upload');
    if (fileInput) fileInput.value = '';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!uploadedFilePath) {
      setError('Please upload a file first');
      return;
    }

    if (!uploadForm.name.trim() || !uploadForm.category.trim() || !uploadForm.date.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    if (userRole === 'state' && (!uploadForm.year || !uploadedThumbnailPath)) {
      setError('Please provide year and thumbnail for training modules');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const payload = {
        name: uploadForm.name.trim(),
        category: uploadForm.category.trim(),
        file: uploadedFilePath,
        date: uploadForm.date,
        ...(userRole === 'state' && { 
          year: uploadForm.year,
          thumbnail: uploadedThumbnailPath 
        })
      };

      console.log('Submitting payload:', payload);

     const response = await fetch(`${api.BASE_URL}/admin/upload/detail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save document');
      }

      const data = await response.json();
      
      if (data && !data.error) {
        setSuccess('Document submitted successfully!');
        
        setUploadForm({
          category: getCurrentTab()?.category || '',
          name: '',
          date: '',
          thumbnail: '',
          year: ''
        });
        setSelectedFile(null);
        setUploadedFilePath(null);
        setSelectedThumbnail(null);
        setUploadedThumbnailPath(null);
        
        const fileInput = window.document.getElementById('file-upload');
        if (fileInput) fileInput.value = '';
        const thumbnailInput = window.document.getElementById('thumbnail-upload');
        if (thumbnailInput) thumbnailInput.value = '';

        await fetchDocuments();
        
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      } else {
        throw new Error(data.message || 'Failed to save document');
      }
    } catch (err) {
      setError('Submission error: ' + err.message);
      console.error('Submission error:', err);
    } finally {
      setSubmitting(false);
    }
  };
 // Delete document
  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      setDeleting(docId);
      setError(null);

      const response = await fetch(`${api.BASE_URL}/admin/upload/${docId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        },
        body: JSON.stringify({
          status: "Delete"
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete document');
      }

      const data = await response.json();
      
      if (data && !data.error) {
        setSuccess('Document deleted successfully!');
        fetchDocuments();
      } else {
        throw new Error(data.message || 'Failed to delete document');
      }
    } catch (err) {
      setError('Delete error: ' + err.message);
      console.error('Delete error:', err);
    } finally {
      setDeleting(null);
    }
  };

  const filteredDocuments = getFilteredDocuments();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Document Management</h1>
        <p className="mt-2 text-sm text-gray-600">Upload and manage your documents</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Upload Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Upload New Document</h2>
          <p className="text-sm text-gray-600 mt-1">Fill in the details and upload your document</p>
        </div>
        
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Document Name */}
            <div>
              <label htmlFor="doc-name" className="block text-sm font-medium text-gray-700 mb-2">
                Document Name *
              </label>
              <input
                type="text"
                id="doc-name"
                value={uploadForm.name}
                onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter document name"
                required
              />
            </div>

            {/* Category (read-only based on tab) */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <input
                type="text"
                id="category"
                value={uploadForm.category}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 cursor-not-allowed"
              />
            </div>

            {/* Date */}
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
                Date *
              </label>
              <input
                type="date"
                id="date"
                value={uploadForm.date}
                onChange={(e) => setUploadForm(prev => ({ ...prev, date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Year field (only for state users) */}
            {userRole === 'state' && (
              <div>
                <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-2">
                  Year *
                </label>
                <input
                  type="text"
                  id="year"
                  value={uploadForm.year}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, year: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter year (e.g., 2025)"
                  required
                />
              </div>
            )}

            {/* Thumbnail Upload (only for state users) */}
            {userRole === 'state' && (
              <div>
                <label htmlFor="thumbnail-upload" className="block text-sm font-medium text-gray-700 mb-2">
                  Thumbnail Image *
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-blue-400 transition-colors">
                  <div className="space-y-1 text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="thumbnail-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                      >
                        <span>Upload thumbnail</span>
                        <input
                          id="thumbnail-upload"
                          name="thumbnail-upload"
                          type="file"
                          className="sr-only"
                          accept="image/*"
                          onChange={handleThumbnailSelect}
                          disabled={uploading}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                  </div>
                </div>

                {selectedThumbnail && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                      <div className="flex items-center">
                        <FileText className={`h-8 w-8 mr-3 ${
                          uploadedThumbnailPath ? 'text-green-500' : 'text-blue-500'
                        }`} />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{selectedThumbnail.name}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(selectedThumbnail.size)}</p>
                          {uploadedThumbnailPath && (
                            <p className="text-xs text-green-600">✓ Uploaded successfully</p>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeThumbnail}
                        disabled={uploading}
                        className="ml-4 text-gray-400 hover:text-red-500 transition-colors disabled:cursor-not-allowed"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* File Upload */}
            <div>
              <label htmlFor="file-upload" className="block text-sm font-medium text-gray-700 mb-2">
                Upload PDF File *
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-blue-400 transition-colors">
                <div className="space-y-1 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                    >
                      <span>Upload a file</span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        className="sr-only"
                        accept=".pdf"
                        onChange={handleFileSelect}
                        disabled={uploading}
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">PDF up to 50MB</p>
                </div>
              </div>

              {selectedFile && (
                <div className="mt-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center">
                      <FileText className={`h-8 w-8 mr-3 ${
                        uploadedFilePath ? 'text-green-500' : 'text-blue-500'
                      }`} />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
                        {uploadedFilePath && (
                          <p className="text-xs text-green-600">✓ Uploaded successfully</p>
                        )}
                        {uploading && (
                          <p className="text-xs text-blue-600">Uploading...</p>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeFile}
                      disabled={uploading}
                      className="ml-4 text-gray-400 hover:text-red-500 transition-colors disabled:cursor-not-allowed"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="rounded-md bg-green-50 p-4">
                <div className="text-sm text-green-700">{success}</div>
              </div>
            )}

            {/* Submit Button */}
            <div>
              <button
                type="submit"
          disabled={
  submitting || 
  !uploadedFilePath || 
  !uploadForm.name.trim() || 
  !uploadForm.category.trim() || 
  !uploadForm.date.trim() ||
  (userRole === 'state' && (!uploadForm.year || !uploadedThumbnailPath))
}
                className={`w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  submitting || 
                  !uploadedFilePath || 
                  !uploadForm.name.trim() || 
                  !uploadForm.category.trim() || 
                  !uploadForm.date.trim() ||
                  (userRole === 'state' && !uploadedThumbnailPath)
                    ? 'bg-gray-300 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                } transition-colors duration-150`}
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Submit Document
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Documents List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {getCurrentTab()?.label} ({filteredDocuments.length})
          </h2>
          <p className="text-sm text-gray-600 mt-1">Manage your uploaded {getCurrentTab()?.label.toLowerCase()}</p>
        </div>

        {loading ? (
          <div className="py-12 text-center">
            <svg className="animate-spin h-8 w-8 text-blue-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-600">Loading dossiers...</p>
          </div>
        ) : filteredDocuments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDocuments.map((document) => (
                  <tr key={document.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <FileText className="h-6 w-6 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{document.name}</div>
                          <div className="text-sm text-gray-500">ID: {document.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                        {formatDate(document.date)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {document.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleView(document)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded-md hover:bg-blue-50 transition-colors"
                          title="View Document"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDownload(document)}
                          className="text-green-600 hover:text-green-900 p-1 rounded-md hover:bg-green-50 transition-colors"
                          title="Download Document"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(document.id)}
                          disabled={deleting === document.id}
                          className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Delete Document"
                        >
                          {deleting === document.id ? (
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-12 text-center">
            <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600">No {getCurrentTab()?.label.toLowerCase()} uploaded yet</p>
            <p className="text-gray-400 text-sm mt-1">Upload your first document using the form above</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dossiers;